import type { JSONContent } from '@tiptap/core'
import type { JsonLike } from '#src/json.ts'
import {
  type Condition,
  conditionSchema,
} from '#src/observation-logic/conditionSchema.ts'
import { instrumentAnswerKey } from '#src/observation-logic/instruments/records.ts'
import {
  draftValue,
  hasDraft,
  panelAnswers,
} from '#src/observation-logic/noteCapture.ts'
import type {
  ShowWhenNode,
  ShowWhenSection,
} from '#src/observation-logic/showWhen.ts'
import type { ObservationValue } from '#src/observation-logic/value.ts'
import { OBSERVATION_MODE_LATEST } from '#src/observationReferences.ts'
import { panelTemplate } from '#src/terminology/instruments/loincPanels.ts'
import { instrumentItems } from '#src/terminology/instruments/registry.ts'

// The tiptap JSON side of the note's show-when conditions, for code with no
// editor (the server signing or rendering a note). The editor's
// ProseMirror-based walker in `@seen/react` mirrors this one.

export const OBSERVATION_NODE = 'observation'
export const CONDITIONAL_SECTION_NODE = 'conditionalSection'
// A run of inline content shown or hidden together, like a section.
export const CONDITIONAL_SPAN_NODE = 'conditionalSpan'
// An instrument panel shows and hides as a whole, like a section.
export const OBSERVATION_PANEL_NODE = 'observation-panel'

export function isSectionLike(name: string | undefined): boolean {
  return (
    name === CONDITIONAL_SECTION_NODE ||
    name === CONDITIONAL_SPAN_NODE ||
    name === OBSERVATION_PANEL_NODE
  )
}

// A section-like node that holds other content (and so encloses it).
export function isConditionalWrapper(name: string | undefined): boolean {
  return name === CONDITIONAL_SECTION_NODE || name === CONDITIONAL_SPAN_NODE
}

// A value conditions read: recorded, unanswered (`null`), or not known yet
// (`'missing'`: still being read, or the read failed).
export type ConditionValue = ObservationValue | null | 'missing'

// Where a node's value is found in the value map: its own record, or, for a
// node frozen to the latest value, the latest record for its code.
export function conditionValueKey(attrs: NodeAttrs): string | null {
  if (attrs.mode === OBSERVATION_MODE_LATEST) {
    return attrs.code
      ? latestValueKey(String(attrs.system ?? ''), String(attrs.code))
      : null
  }
  return typeof attrs.reference === 'string' && attrs.reference
    ? attrs.reference
    : null
}

export function latestValueKey(system: string, code: string): string {
  return `latest:${system}|${code}`
}

// Where an instrument capture's record for a code is found in the value
// map; with no coding, the marker that the group is still being read.
export function groupValueKey(
  groupId: string,
  coding?: { system?: string; code?: string }
): string {
  return coding
    ? `group:${groupId}|${coding.system ?? ''}|${coding.code ?? ''}`
    : `group:${groupId}`
}

// The total a panel frozen to the latest value shows: the participant's
// latest record of the instrument's total code, found in the value map at
// its `latestValueKey`. None for a panel capturing answers, or an
// instrument without a total.
export function frozenPanelTotal(
  attrs: NodeAttrs
): { system: string; code: string } | null {
  if (attrs.mode !== OBSERVATION_MODE_LATEST) {
    return null
  }
  const coding = panelTemplate(attrs)?.total?.coding
  return coding?.code
    ? { system: coding.system ?? '', code: coding.code }
    : null
}

// The attributes an observation node holds in the note.
export interface NodeAttrs {
  readonly [name: string]: JsonLike | undefined
}

// A stored condition, or none when it is missing or not a condition.
export function readCondition(
  raw: JsonLike | undefined
): Condition | undefined {
  if (!raw) {
    return undefined
  }
  const parsed = conditionSchema.safeParse(raw)
  return parsed.success ? parsed.data : undefined
}

export interface DocumentUnits {
  nodes: ShowWhenNode[]
  sections: ShowWhenSection[]
}

// A multi-select node's chosen answers, from the codes it holds (one record
// each): a condition on it matches any of them.
export function answeredSet(
  attrs: NodeAttrs
): Pick<ShowWhenNode, 'value'> | undefined {
  if (
    !attrs.repeats ||
    attrs.mode === OBSERVATION_MODE_LATEST ||
    !Array.isArray(attrs.references) ||
    attrs.references.length === 0
  ) {
    return undefined
  }
  return {
    value: {
      kind: 'coding',
      value: attrs.references.flatMap((answer: { code?: unknown }) =>
        typeof answer?.code === 'string' ? [{ code: answer.code }] : []
      ),
    },
  }
}

function nodeValue(
  value: ConditionValue | undefined
): Pick<ShowWhenNode, 'value' | 'pending'> {
  return value === 'missing'
    ? { value: null, pending: true }
    : { value: value ?? null }
}

// An instrument panel's items and total as observations conditions can
// read, inside the panel (hidden with it), from its written records.
function panelUnits(
  attrs: NodeAttrs,
  values: ReadonlyMap<string, ConditionValue>
): ShowWhenNode[] {
  const template = panelTemplate(attrs)
  const panelId = String(attrs.id)
  if (!template) {
    return []
  }
  const groupId = typeof attrs.groupId === 'string' ? attrs.groupId : null
  // Answers held in the note are the panel's until it is signed.
  const held = panelAnswers(attrs)
  const recorded = (coding: { system?: string; code?: string }) =>
    nodeValue(
      held
        ? (held[instrumentAnswerKey(coding)] ?? null)
        : groupId
          ? values.has(groupValueKey(groupId))
            ? 'missing'
            : values.get(groupValueKey(groupId, coding))
          : null
    )
  const frozenTotal = frozenPanelTotal(attrs)
  const total = template.total
    ? [
        {
          coding: template.total.coding,
          ...(frozenTotal
            ? nodeValue(
                values.get(latestValueKey(frozenTotal.system, frozenTotal.code))
              )
            : recorded(template.total.coding)),
        },
      ]
    : []
  return [
    ...instrumentItems(template).map((item) => ({
      coding: item.coding,
      ...recorded(item.coding),
    })),
    ...total,
  ].map(({ coding, ...value }) => {
    const unit: ShowWhenNode = {
      id: `${panelId}/${instrumentAnswerKey(coding)}`,
      code: coding.code ?? '',
      sectionId: panelId,
      ...value,
    }
    if (coding.system) {
      unit.system = coding.system
    }
    return unit
  })
}

// The observations and section-like nodes (conditional sections, instrument
// panels) of a tiptap JSON document, in order, as `evaluateShowWhen` reads
// them. Only a conditional section or span encloses other nodes.
export function collectDocumentUnits(
  doc: JSONContent,
  values: ReadonlyMap<string, ConditionValue>
): DocumentUnits {
  const nodes: ShowWhenNode[] = []
  const sections: ShowWhenSection[] = []
  const visit = (node: JSONContent, sectionId: string | undefined): void => {
    const attrs = node.attrs ?? {}
    if (isSectionLike(node.type) && attrs.id) {
      const showWhen = readCondition(attrs.showWhen)
      const section: ShowWhenSection = { id: attrs.id, index: nodes.length }
      if (sectionId) {
        section.parentId = sectionId
      }
      if (showWhen) {
        section.showWhen = showWhen
      }
      sections.push(section)
      if (node.type === OBSERVATION_PANEL_NODE) {
        nodes.push(...panelUnits(attrs, values))
      }
      const inner = isConditionalWrapper(node.type) ? attrs.id : sectionId
      for (const child of node.content ?? []) {
        visit(child, inner)
      }
      return
    }
    if (node.type === OBSERVATION_NODE && attrs.id) {
      const showWhen = readCondition(attrs.showWhen)
      const key = conditionValueKey(attrs)
      const unit: ShowWhenNode = {
        id: attrs.id,
        code: attrs.code ?? '',
        ...(hasDraft(attrs)
          ? { value: draftValue(attrs) }
          : (answeredSet(attrs) ?? nodeValue(key ? values.get(key) : null))),
      }
      if (attrs.system) {
        unit.system = attrs.system
      }
      if (attrs.name) {
        unit.name = attrs.name
      }
      if (sectionId) {
        unit.sectionId = sectionId
      }
      if (showWhen) {
        unit.showWhen = showWhen
      }
      nodes.push(unit)
      return
    }
    for (const child of node.content ?? []) {
      visit(child, sectionId)
    }
  }
  visit(doc, undefined)
  return { nodes, sections }
}

export interface ConditionValueTargets {
  // Whether the document has any show-when condition.
  conditional: boolean
  // Observation references whose record a condition may read.
  references: string[]
  // Codes whose latest record a frozen node (or a frozen panel's total)
  // reads.
  latest: { system: string; code: string }[]
  // Instrument captures whose records a condition may read, by group id.
  groups: string[]
}

const NO_TARGETS: ConditionValueTargets = {
  conditional: false,
  references: [],
  latest: [],
  groups: [],
}

// What a document's conditions could read, or nothing when it has no
// condition at all.
export function conditionValueTargets(doc: JSONContent): ConditionValueTargets {
  let conditional = false
  const references = new Set<string>()
  const latest = new Map<string, { system: string; code: string }>()
  const groups = new Set<string>()
  const visit = (node: JSONContent): void => {
    const attrs = node.attrs ?? {}
    if (attrs.showWhen) {
      conditional = true
    }
    if (
      node.type === OBSERVATION_PANEL_NODE &&
      attrs.groupId &&
      !panelAnswers(attrs)
    ) {
      groups.add(attrs.groupId)
    }
    const frozenTotal =
      node.type === OBSERVATION_PANEL_NODE ? frozenPanelTotal(attrs) : null
    if (frozenTotal) {
      latest.set(
        latestValueKey(frozenTotal.system, frozenTotal.code),
        frozenTotal
      )
    }
    if (node.type === OBSERVATION_NODE) {
      const key = conditionValueKey(attrs)
      if (key && attrs.mode === OBSERVATION_MODE_LATEST) {
        latest.set(key, {
          system: String(attrs.system ?? ''),
          code: String(attrs.code),
        })
      } else if (key && !hasDraft(attrs)) {
        references.add(key)
      }
      return
    }
    for (const child of node.content ?? []) {
      visit(child)
    }
  }
  visit(doc)
  if (!conditional) {
    return NO_TARGETS
  }
  return {
    conditional,
    references: [...references].sort(),
    latest: [...latest.values()],
    groups: [...groups].sort(),
  }
}

// The document without the observation nodes, sections and instrument panels
// whose ids are in `hidden`, as a live note shows it. Other nodes with an id
// are kept.
export function pruneHiddenContent(
  doc: JSONContent,
  hidden: ReadonlySet<string>
): JSONContent {
  if (!hidden.size) {
    return doc
  }
  const prune = (node: JSONContent): JSONContent | null => {
    const id = node.attrs?.id
    const prunable = isSectionLike(node.type) || node.type === OBSERVATION_NODE
    if (prunable && typeof id === 'string' && hidden.has(id)) {
      return null
    }
    if (!node.content) {
      return node
    }
    return {
      ...node,
      content: node.content.flatMap((child) => {
        const kept = prune(child)
        return kept ? [kept] : []
      }),
    }
  }
  return prune(doc) ?? { type: 'doc', content: [] }
}
